import React, { ReactElement, ReactNode } from 'react'
import { Flex, Image, Text, ThemeUIStyleObject } from 'theme-ui'
import { TextWithBreakpointVariant } from 'ui/TextWithBreakpointVariant'
import { Button, Svg } from 'ui'
import { navigate } from 'gatsby'
import { orgStaticPageHeaderHeight } from 'components/NewStaticPage/StaticPageHeader'

export default function CommonSectionItem({
  image,
  customImage,
  title,
  description,
  isReverse,
  additionalDescriptionContent,
  useCases,
  learnMorePath,
  sx,
  sxTextContainer,
  shouldMakeImageSticky,
}: {
  image?: string
  customImage?: ReactNode
  title: string
  description?: string | ReactNode
  isReverse?: boolean
  additionalDescriptionContent?: ReactNode
  useCases?: ReactNode
  learnMorePath?: string
  sx?: ThemeUIStyleObject
  sxTextContainer?: ThemeUIStyleObject
  shouldMakeImageSticky?: boolean
}): ReactElement {
  return (
    <Flex
      sx={{
        maxWidth: ['100%', '102.2rem'],
        width: '100%',
        gap: ['2.4rem', '1.6rem', '4rem'],
        alignItems: 'center',
        flexDirection: ['column', isReverse ? 'row-reverse' : 'row'],
        justifyContent: 'space-between',
        ...sx,
      }}
    >
      <Flex
        sx={{
          maxWidth: ['100%', '49.1rem'],
          width: '100%',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          ...(shouldMakeImageSticky && {
            position: ['static', 'sticky'],
            top: ['unset', orgStaticPageHeaderHeight],
          }),
        }}
      >
        {image && (
          <Image
            src={image}
            sx={{
              flexShrink: 'inherit',
              height: '100%',
              width: '100%',
              maxHeight: '40rem',
              maxWidth: '49.1rem',
            }}
            loading="lazy"
          />
        )}
        {customImage}
      </Flex>
      <Flex
        sx={{
          flex: ['unset', 1],
          flexDirection: 'column',
          gap: ['0.8rem', '1.6rem'],
          alignItems: 'flex-start',
          textAlign: 'left',
          px: ['1.6rem', 0],
          ...sxTextContainer,
        }}
      >
        <TextWithBreakpointVariant
          variant={['size24Weight600', 'size28Weight600']}
          sx={{ color: 'aliceBlue' }}
        >
          {title}
        </TextWithBreakpointVariant>
        {!!description && (
          <Flex sx={{ flexDirection: 'column', gap: ['1.8rem', '3.2rem'] }}>
            <Text variant="size16Weight400Line140Letter08" sx={{ color: 'wildBlueYonder' }}>
              {description}
            </Text>
            {additionalDescriptionContent}
          </Flex>
        )}
        {useCases && <Flex sx={{ mt: '0.8rem', flexDirection: 'column' }}>{useCases}</Flex>}
        {learnMorePath && (
          <Button
            ariaLabel="Ready Case Study"
            name="Ready Case Study"
            data-testid="ready-case-study-button"
            sx={{
              mt: '2.4rem',
            }}
            variant="roundedSecondary"
            onClick={() => navigate(learnMorePath)}
          >
            <Text variant="size15Weight600Line138">Learn more</Text>
          </Button>
        )}
      </Flex>
    </Flex>
  )
}

export function CustomImage({
  svg,
  descriptiveText,
}: {
  svg: React.ElementType
  descriptiveText: string
}): ReactElement {
  return (
    <Flex
      sx={{
        height: '100%',
        width: '100%',
        maxHeight: '40rem',
        maxWidth: '49.1rem',
        border: '1px solid',
        borderRadius: '1.2rem',
        borderColor: 'blueYonder20',
        overflow: 'hidden',
        mx: ['1.6rem', 0],
      }}
    >
      <Svg
        svg={svg}
        descriptiveText={descriptiveText}
        sx={{
          flexShrink: 'inherit',
          height: '100%',
          width: '100%',
        }}
      />
    </Flex>
  )
}
